import type {
	ImpressionEntry,
	ImpressionsEventPayload,
	ListingEventContext,
	ListingTrackingContext,
	UseListingEventTrackingReturn
} from '~/composables/useListingEventTracking';
import type { TrackingIdentity } from '~/composables/visitorIdentity';
import type { Ref } from 'vue';

import {
	buildBaseListingEventPayload,
	useListingEventTracking
} from '~/composables/useListingEventTracking';
import { useTrackingIdentity } from '~/composables/visitorIdentity';
import { watch } from 'vue';

const VIEWPORT_THRESHOLD = 0.5;
const MIN_VISIBLE_MS = 500;
const FLUSH_DEBOUNCE_MS = 1000;
const MAX_BATCH_SIZE = 20;
const ENDPOINT = '/api/listing-events';

export interface ImpressionCardData {
	propertyId: string;
	trackingContext: ListingTrackingContext;
	positionInList: number;
}

interface ElementState {
	supplier: () => ImpressionCardData;
	enterTimer: ReturnType<typeof setTimeout> | null;
}

interface QueuedImpression {
	entry: ImpressionEntry;
	context: ListingEventContext;
}

interface ContextGroup {
	context: ListingEventContext;
	impressions: ImpressionEntry[];
}

// Module-level singletons: every PropertyCard shares the same observer, queue
// and dedup Set so MAX_BATCH_SIZE and the debounce window apply page-wide.
// Global listeners live for the tab lifetime to flush on unload across SPA
// navigations; they're torn down only by the test reset helper.
const elementState = new WeakMap<Element, ElementState>();
const seenInView = new Set<string>();
const queue: QueuedImpression[] = [];
let currentViewKey = '';
let observer: IntersectionObserver | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let listenersInstalled = false;
let unregisterRouterHook: (() => void) | null = null;
let routeRef: ReturnType<typeof useRoute> | null = null;
let trackImpressionsRef:
	| UseListingEventTrackingReturn['trackImpressions']
	| null = null;
let getTrackingIdentityRef: (() => TrackingIdentity) | null = null;

// Placement is part of the key so that a property appearing in multiple
// carousels on the same page (home, PDP) emits one impression per carousel.
function viewKeyOf(url: string, ctx: ListingTrackingContext): string {
	const sortedKeys = Object.keys(ctx.applied_filters).sort();
	const stableFilters = JSON.stringify(ctx.applied_filters, sortedKeys);
	return `${url}|${ctx.placement}|${ctx.page}|${stableFilters}`;
}

function dedupKeyOf(propertyId: string, placement: string): string {
	return `${placement}:${propertyId}`;
}

function handleVisibilityChange(): void {
	if (document.visibilityState === 'hidden') flushViaBeacon();
}

function handlePageHide(): void {
	flushViaBeacon();
}

function installGlobalListeners(): void {
	if (listenersInstalled || typeof document === 'undefined') return;

	// Capture composables here while we're still inside a Vue setup call from
	// `useImpressionBatch`; the later flush paths run from timers/router hooks
	// /lifecycle events where the implicit Nuxt context is gone.
	routeRef = useRoute();
	const tracking = useListingEventTracking();
	trackImpressionsRef = tracking.trackImpressions;
	getTrackingIdentityRef = useTrackingIdentity();

	observer = new IntersectionObserver(handleObserverEntries, {
		threshold: VIEWPORT_THRESHOLD
	});

	document.addEventListener('visibilitychange', handleVisibilityChange);
	window.addEventListener('pagehide', handlePageHide);

	unregisterRouterHook = useRouter().beforeEach((_to, _from, next) => {
		flushNow();
		next();
	});

	listenersInstalled = true;
}

function handleObserverEntries(entries: IntersectionObserverEntry[]): void {
	for (const entry of entries) {
		const state = elementState.get(entry.target);
		if (!state) continue;

		const isVisibleEnough =
			entry.isIntersecting && entry.intersectionRatio >= VIEWPORT_THRESHOLD;

		if (isVisibleEnough) {
			if (state.enterTimer) continue;
			state.enterTimer = setTimeout(() => {
				state.enterTimer = null;
				recordImpression(state);
			}, MIN_VISIBLE_MS);
		} else if (state.enterTimer) {
			clearTimeout(state.enterTimer);
			state.enterTimer = null;
		}
	}
}

function recordImpression(state: ElementState): void {
	const card = state.supplier();
	const url = routeRef?.path ?? window.location.pathname;
	const newViewKey = viewKeyOf(url, card.trackingContext);

	if (newViewKey !== currentViewKey) {
		seenInView.clear();
		currentViewKey = newViewKey;
	}

	const dedupKey = dedupKeyOf(card.propertyId, card.trackingContext.placement);
	if (seenInView.has(dedupKey)) return;
	seenInView.add(dedupKey);

	const { trackingContext, positionInList } = card;

	queue.push({
		entry: {
			property_id: card.propertyId,
			position: positionInList + 1,
			entered_viewport_at: new Date().toISOString()
		},
		context: { ...trackingContext, url }
	});

	scheduleFlush();
}

function scheduleFlush(): void {
	if (queue.length >= MAX_BATCH_SIZE) {
		flushNow();
		return;
	}
	if (debounceTimer) clearTimeout(debounceTimer);
	debounceTimer = setTimeout(flushNow, FLUSH_DEBOUNCE_MS);
}

function drainAndGroup(): ContextGroup[] {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}

	const items = queue.splice(0);
	const groups = new Map<string, ContextGroup>();
	for (const item of items) {
		const key = JSON.stringify(item.context);
		let group = groups.get(key);
		if (!group) {
			group = { context: item.context, impressions: [] };
			groups.set(key, group);
		}
		group.impressions.push(item.entry);
	}
	return Array.from(groups.values());
}

function flushNow(): void {
	const groups = drainAndGroup();
	if (groups.length === 0 || !trackImpressionsRef) return;

	for (const group of groups) {
		trackImpressionsRef({
			context: group.context,
			impressions: group.impressions
		});
	}
}

function flushViaBeacon(): void {
	const groups = drainAndGroup();
	if (groups.length === 0) return;

	const canBeacon =
		typeof navigator !== 'undefined' &&
		typeof navigator.sendBeacon === 'function';
	if (!canBeacon) {
		flushNowFromGroups(groups);
		return;
	}

	if (!getTrackingIdentityRef) return;
	const identity = getTrackingIdentityRef();
	for (const group of groups) {
		const payload: ImpressionsEventPayload = {
			...buildBaseListingEventPayload(identity),
			event_type: 'impression',
			context: group.context,
			impressions: group.impressions
		};
		const blob = new Blob([JSON.stringify(payload)], {
			type: 'application/json'
		});
		navigator.sendBeacon(ENDPOINT, blob);
	}
}

function flushNowFromGroups(groups: ContextGroup[]): void {
	if (!trackImpressionsRef) return;
	for (const group of groups) {
		trackImpressionsRef({
			context: group.context,
			impressions: group.impressions
		});
	}
}

// `supplier` is invoked at impression time so position/context reflect the
// card's current state (filters or pagination may have changed during the
// 500ms visibility window).
export function useImpressionBatch(
	elementRef: Ref<HTMLElement | null>,
	supplier: () => ImpressionCardData
): void {
	if (typeof window === 'undefined') return;

	installGlobalListeners();
	if (!observer) return;

	watch(elementRef, (el, _prev, onCleanup) => {
		if (!el || !observer) return;

		elementState.set(el, { supplier, enterTimer: null });
		observer.observe(el);

		onCleanup(() => {
			const state = elementState.get(el);
			if (state?.enterTimer) clearTimeout(state.enterTimer);
			elementState.delete(el);
			observer?.unobserve(el);
		});
	});
}

export function __resetImpressionBatchForTests(): void {
	if (debounceTimer) clearTimeout(debounceTimer);
	debounceTimer = null;
	queue.length = 0;
	seenInView.clear();
	currentViewKey = '';
	observer?.disconnect();
	observer = null;

	if (listenersInstalled && typeof document !== 'undefined') {
		document.removeEventListener('visibilitychange', handleVisibilityChange);
		window.removeEventListener('pagehide', handlePageHide);
	}
	unregisterRouterHook?.();
	unregisterRouterHook = null;

	listenersInstalled = false;
	routeRef = null;
	trackImpressionsRef = null;
	getTrackingIdentityRef = null;
}
